import { DateTime } from 'luxon';
import { z } from 'zod/v4-mini';

import {
  ConditionType,
  MedicationSchema,
  conditionTypeSchema,
  medicationSchemeSchema,
} from '@/models/ConditionModel.ts';
import { FileModel, fileSchema } from '@/models/FileModel.ts';
import {
  TeleMonitoringCriteria,
  telemonitoringCriteriaSchema,
} from '@/models/TelemonitoringCriteriaModel.ts';
import { Schema, cz } from '@/utils/zodUtils.ts';

type CarePlanDuration = '1' | '2' | '3';

export type Type1PenCarePlanRenewalInfo = {
  diabetesType: 'type1';
  insulinScheme: 'insulinPen';
  termDate: undefined;
  inclusionCriteria: TeleMonitoringCriteria;
  duration: CarePlanDuration;
  description?: string;
  descriptionAttachment?: FileModel;
};

export type Type2PenCarePlanRenewalInfo = {
  diabetesType: 'type2';
  insulinScheme: 'insulinPen';
  termDate: undefined;
  inclusionCriteria: TeleMonitoringCriteria;
  duration: CarePlanDuration;
  description?: string;
  descriptionAttachment?: FileModel;
};

export type Type1PumpCarePlanRenewalInfo = {
  diabetesType: 'type1';
  insulinScheme: 'insulinPump';
  termDate: undefined;
  inclusionCriteria: TeleMonitoringCriteria;
  duration: CarePlanDuration;
  description?: string;
  descriptionAttachment?: FileModel;
};

export type Type2PumpCarePlanRenewalInfo = {
  diabetesType: 'type2';
  insulinScheme: 'insulinPump';
  termDate: undefined;
  inclusionCriteria: TeleMonitoringCriteria;
  duration: CarePlanDuration;
  description?: string;
  descriptionAttachment?: FileModel;
};

export type GestationalPenCarePlanRenewalInfo = {
  diabetesType: 'gestational';
  insulinScheme: 'insulinPen';
  termDate: string;
  inclusionCriteria: TeleMonitoringCriteria;
  duration: CarePlanDuration;
  description?: string;
  descriptionAttachment?: FileModel;
};

export type CarePlanRenewalInfo =
  | Type1PenCarePlanRenewalInfo
  | Type2PenCarePlanRenewalInfo
  | Type1PumpCarePlanRenewalInfo
  | Type2PumpCarePlanRenewalInfo
  | GestationalPenCarePlanRenewalInfo;

export type SimpleCarePlanRenewalInfo = {
  diabetesType: ConditionType;
  insulinScheme: MedicationSchema;
  termDate?: string;
  inclusionCriteria: TeleMonitoringCriteria;
  duration: CarePlanDuration;
  description?: string;
  descriptionAttachment?: FileModel;
};

const cannotBeGestationalAndUsePump = (data: SimpleCarePlanRenewalInfo) => {
  if (data.diabetesType === 'gestational') {
    return data.insulinScheme !== 'insulinPump';
  }
  return true;
};

const requiredTermDateIfGestational = (data: SimpleCarePlanRenewalInfo) => {
  if (data.diabetesType === 'gestational') {
    return !!data.termDate;
  }
  return true;
};

const requiredDescription = (data: SimpleCarePlanRenewalInfo) => {
  return !!data.description || !!data.descriptionAttachment;
};

export const carePlanRenewalSchema: Schema<SimpleCarePlanRenewalInfo> = z
  .object(
    {
      diabetesType: conditionTypeSchema,
      insulinScheme: medicationSchemeSchema,
      termDate: z.optional(
        z.iso
          .date('form.required')
          .check(cz.validation.dateAfter(DateTime.now(), 'form.date.past')),
      ),
      inclusionCriteria: telemonitoringCriteriaSchema,
      duration: z.literal(['1', '2', '3']),
      description: z.optional(z.string()),
      descriptionAttachment: z.optional(fileSchema),
    },
    'forms.required',
  )
  .check(
    z.refine(cannotBeGestationalAndUsePump, {
      path: ['insulinSchema'],
      error: 'form.required',
    }),
    z.refine(requiredTermDateIfGestational, {
      path: ['termDate'],
      error: 'form.required',
    }),
    z.refine(requiredDescription, {
      path: ['description'],
      error: 'diabetesForms.eitherObjectiveOrFile',
    }),
  );
